import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { setMedarbetare } from '../../api/medarbetareApi'
import { useApi } from '../../api/useApi'
import { useLatestRunMonth } from '../../api/latestRunApi'
import ViewHeader from '../../components/viewHeader/ViewHeader'
import FilterToolbar from '../../components/filterToolbar/FilterToolbar'
import { CreateFilterConfig } from '../../components/tablesConfig/filterTableConfig'
import { DataType } from '../../components/tablesConfig/filterconfig'
import { HanteringEntry, LÄGE_FILTER } from '../../components/tablesConfig/hanteringColumn'
import { setCaseAction } from '../arende/store/caseReducer/actions'
import HandleFlagModal from '../arende/components/handleFlag/HandleFlagModal'
import { selectMedLoading, selectMedarbetare } from './store/selectors'

// Antd
import { Button, Spin, Table, Tooltip } from 'antd'
import type { TableProps } from 'antd/es/table'
import { getPersonalDateFrom, getPersonalDateTo, setPersonalDate } from '../../store/personalDateSlice'
import { getPersonalDeselectedColumns, getPersonalFilter, setPersonalDeselectedColumns, setPersonalFilter } from '../../store/personalFilterSlice'
import { useColumnVisibility } from '../../components/tablesConfig/useColumnVisibility'
import '../../styles/flagLink.css'

export type IMedarbetare = {
  id: number
  flagga: number
  flaggnamn: string
  nivå: number
  namn: string
  // Personens id i Pejlen, som personalsidan adresseras på. `id` är flaggans (#99)
  medarbetare_id: number
  från: Date
  till: Date
  created: Date
  utförare: {
    utförare_id: number
    utförare_namn: string
  }[]
  hantering?: HanteringEntry
}

const Medarbetare: React.FC = () => {
  const [btnDisabled, setBtnDisabled] = useState<boolean>(true)
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [selectedRows, setSelectedRows] = useState<DataType[]>([])
  const [handleOpen, setHandleOpen] = useState<boolean>(false)
  const [pageSize, setPageSize] = useState<number>(200)
  const dispatch = useDispatch()
  const dispatchCallback = useCallback(dispatch, [dispatch])
  // #47: datepicker-default = senaste körningens månad (ej nuvarande), men användarens egna val
  // bibehålls i Redux-slicen (överlever navigering). null = inget val ännu → senaste körningen.
  // Slicen seedas i hämtnings-effekten så arv (individsida) alltid har aktuellt datum.
  const { ready: lrReady, from: lrFrom, to: lrTo } = useLatestRunMonth()
  const sliceFrom = useSelector(getPersonalDateFrom)
  const sliceTo = useSelector(getPersonalDateTo)
  const startDate = sliceFrom ?? lrFrom
  const endDate = sliceTo ?? lrTo
  const api = useApi()

  // Redux states
  const medarbetareState = useSelector(selectMedarbetare)
  const loadingState = useSelector(selectMedLoading)
  const filterData = useSelector(getPersonalFilter) as { filter?: any; sorter?: any } | undefined
  const activeFilter = filterData?.filter ?? {}

  // Sortering sköts av kolumnhuvudena → onChange uppdaterar bara sorter (popover-filtret behålls).
  const handleChange: TableProps<DataType>['onChange'] = (pagination, filters, sorter) => {
    dispatch(setPersonalFilter({ data: { ...(filterData ?? {}), sorter } }))
  }

  // Popover-filter per dimension (sökbara multi-selects, som dagens kolumn-trattar). Filter-state
  // lever kvar i personalFilterSlice → bevaras vid navigering precis som tidigare.
  const setFilterPart = (key: string, values: (string | number)[]) =>
    dispatch(setPersonalFilter({ data: { ...(filterData ?? {}), filter: { ...activeFilter, [key]: values } } }))
  const resetFilters = () => dispatch(setPersonalFilter({ data: { ...(filterData ?? {}), filter: {} } }))

  const rows = (medarbetareState ?? []) as any[]
  // Filter-alternativen (Set+sort över alla rader) är dyra — memoisera per datamängd.
  const filterOptions = useMemo(() => {
    const uniqStr = (vals: any[]) =>
      Array.from(new Set(vals.filter((v): v is string => !!v)))
        .sort((a, b) => a.localeCompare(b))
        .map((v) => ({ value: v, label: v }))
    return {
      name: uniqStr(rows.map((r) => r.namn)),
      deviation: uniqStr(rows.map((r) => r.flaggnamn)),
      risk: Array.from(new Set(rows.map((r) => r.nivå)))
        .sort((a, b) => (a as number) - (b as number))
        .map((v) => ({ value: v as number, label: v as number })),
      utförare: uniqStr(rows.map((r) => (r.utförare ?? []).map((u: any) => u.utförare_namn)).flat()),
    }
  }, [medarbetareState])
  const filterSelects = [
    { key: 'name', label: 'Namn', options: filterOptions.name, value: activeFilter.name ?? [], onChange: (v: any) => setFilterPart('name', v) },
    { key: 'deviation', label: 'Flagga', options: filterOptions.deviation, value: activeFilter.deviation ?? [], onChange: (v: any) => setFilterPart('deviation', v) },
    { key: 'risk', label: 'Risknivå', options: filterOptions.risk, value: activeFilter.risk ?? [], onChange: (v: any) => setFilterPart('risk', v) },
    { key: 'utförare', label: 'Utförare', options: filterOptions.utförare, value: activeFilter.utförare ?? [], onChange: (v: any) => setFilterPart('utförare', v) },
    // Fast alternativlista, inte härledd ur datan som de övriga: lägena är tre och alltid
    // desamma. Härledde vi dem ur raderna skulle "Pågående" försvinna ur filtret så fort
    // ingen flagga råkade vara pågående, vilket är precis när man vill kunna välja det.
    { key: 'hantering', label: 'Status', options: LÄGE_FILTER, value: activeFilter.hantering ?? [], onChange: (v: any) => setFilterPart('hantering', v) },
  ]

  // Klient-sidefiltrering: OR inom en dimension, AND mellan dimensioner (som trattarna).
  // useMemo: räkna bara om när data eller filter ändras (inte vid sidbyte/övriga renders).
  const filteredData = useMemo(() => {
    const af = filterData?.filter ?? {}
    return rows.filter(
      (r) =>
        (!af.name?.length || af.name.some((v: string) => (r.namn ?? '').includes(v))) &&
        (!af.deviation?.length || af.deviation.includes(r.flaggnamn)) &&
        (!af.risk?.length || af.risk.includes(r.nivå)) &&
        (!af.utförare?.length || (r.utförare ?? []).some((u: any) => af.utförare.includes(u.utförare_namn))) &&
        (!af.hantering?.length || af.hantering.includes(r.hantering?.läge ?? 'ej_hanterad'))
    )
  }, [medarbetareState, filterData])

  function handleDatesChange(range: any) {
    const start = range[0].startOf('month').format().split('T')[0]
    const end = range[1].endOf('month').format().split('T')[0]
    dispatch(setPersonalDate({ from: start, to: end }))
  }

  function handleOpenChange() {
    return undefined
  }

  // Hämta flagg-data först när latest-run är klart → aldrig en hämtning med fallback-månaden (#47).
  useEffect(() => {
    if (!lrReady) return
    setMedarbetare(api, dispatchCallback, { from: startDate, to: endDate })
    dispatch(setPersonalDate({ from: startDate, to: endDate }))
  }, [lrReady, startDate, endDate])

  useEffect(() => {
    if (loadingState && lrReady) {
      setMedarbetare(api, dispatch, { from: startDate, to: endDate })
    }
  }, [loadingState, api, dispatch])

  // Rensa radval när datafönstret eller filtret ändras — annars hänger val kvar på rader
  // som inte längre visas (spök-val över sidor).
  useEffect(() => {
    setSelectedRowKeys([])
    setBtnDisabled(true)
    setCaseAction('SET_CASE', [], null, dispatch)
  }, [startDate, endDate, JSON.stringify(activeFilter)])

  const allColumns = CreateFilterConfig(medarbetareState || [])
  const { visibleColumns, deselected, setDeselected, selectOptions } = useColumnVisibility(
    allColumns,
    getPersonalDeselectedColumns,
    setPersonalDeselectedColumns
  )

  const SELECTION_COLUMN_WIDTH = 32
  const scrollX = visibleColumns.reduce((sum, col) => sum + ((col.width as number) || 0), 0) + SELECTION_COLUMN_WIDTH

  const rowSelection = {
    fixed: 'right' as const,
    preserveSelectedRowKeys: true,
    selectedRowKeys,
    onChange: (keys: React.Key[], rader: DataType[]) => {
      setSelectedRowKeys(keys)
      setSelectedRows(rader)
      setBtnDisabled(keys.length === 0)
      setCaseAction('SET_CASE', rader, null, dispatch)
    },
  }

  // Efter sparad hantering: släpp radvalen och hämta om, så att Status-kolumnen speglar
  // det som just registrerades i stället för gammal data. Samma som Brukare-listan.
  const onHandled = () => {
    setHandleOpen(false)
    setSelectedRowKeys([])
    setSelectedRows([])
    setBtnDisabled(true)
    setCaseAction('SET_CASE', [], null, dispatch)
    setMedarbetare(api, dispatch, { from: startDate, to: endDate })
  }

  if (!lrReady) {
    return (
      <>
        <ViewHeader heading="Personal" description="Denna vy listar flaggor som rör utförarnas personal." />
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 80 }}>
          <Spin />
        </div>
      </>
    )
  }

  return (
    <>
      <ViewHeader heading="Personal" description="Denna vy listar flaggor som rör utförarnas personal." />
      <FilterToolbar
        startDate={startDate}
        endDate={endDate}
        onDatesChange={handleDatesChange}
        onOpenChange={handleOpenChange}
        filterSelects={filterSelects}
        onResetFilters={resetFilters}
        columnOptions={selectOptions}
        deselectedColumns={deselected}
        onDeselectedColumnsChange={setDeselected}
        total={`${filteredData.length} av ${rows.length}`}
        action={
          <Tooltip overlayClassName="flag-tooltip" title={btnDisabled ? <>Välj en eller flera rader<br />i tabellen nedan</> : ''}>
            <span style={btnDisabled ? { cursor: 'not-allowed' } : undefined}>
              <Button type="primary" onClick={() => setHandleOpen(true)} disabled={btnDisabled} style={{ width: 165, padding: '0 8px', ...(btnDisabled ? { pointerEvents: 'none' } : {}) }}>
                Hantera flagga{selectedRowKeys.length > 0 ? ` (${selectedRowKeys.length})` : ''}
              </Button>
            </span>
          </Tooltip>
        }
      />
      {medarbetareState && (
        <Table
          className="flag-table"
          scroll={{ x: scrollX, y: filteredData.length > 200 ? 'calc(100vh - 305px)' : 'calc(100vh - 255px)' }}
          columns={visibleColumns}
          rowSelection={{ ...rowSelection }}
          dataSource={filteredData}
          bordered
          size="small"
          onChange={handleChange}
          pagination={filteredData.length > 200 ? { pageSize, pageSizeOptions: ['50', '100', '200'], showSizeChanger: true, onShowSizeChange: (_: number, size: number) => setPageSize(size) } : false}
          loading={loadingState}
        />
      )}
      <HandleFlagModal open={handleOpen} rows={selectedRows} onClose={() => setHandleOpen(false)} onDone={onHandled} />
    </>
  )
}

export default Medarbetare
